import { DropDownEditableProps, EditableOption } from '@/shared/types/editable';
import { MtdItQuarters } from '@/shared/types/enum';
import { formatIsoDateTime } from '@/shared/utils/date';
import { formatDate } from '@/shared/utils/format';

export const MTD_IT_DROPDOWN_MIN_TAX_YEAR = 2026;
export const MTD_IT_DROPDOWN_YEARS_BACK = 5;
export const MTD_IT_DROPDOWN_YEARS_FORWARD = 5;

export type MtdItQuarterKind = 'start' | 'end';

export type MtdItQuarterNumber = 1 | 2 | 3 | 4;

export type MtdItQuarterId = {
  taxYearStart: number;
  quarter: MtdItQuarterNumber;
};

type MonthDay = { month: number; day: number; yearOffset: 0 | 1 };

const TAX_YEAR_BOUNDARIES: Record<MtdItQuarterNumber, { start: MonthDay; end: MonthDay }> = {
  1: { start: { month: 4, day: 6, yearOffset: 0 }, end: { month: 7, day: 5, yearOffset: 0 } },
  2: { start: { month: 7, day: 6, yearOffset: 0 }, end: { month: 10, day: 5, yearOffset: 0 } },
  3: { start: { month: 10, day: 6, yearOffset: 0 }, end: { month: 1, day: 5, yearOffset: 1 } },
  4: { start: { month: 1, day: 6, yearOffset: 1 }, end: { month: 4, day: 5, yearOffset: 1 } },
};

const CALENDAR_BOUNDARIES: Record<MtdItQuarterNumber, { start: MonthDay; end: MonthDay }> = {
  1: { start: { month: 4, day: 1, yearOffset: 0 }, end: { month: 6, day: 30, yearOffset: 0 } },
  2: { start: { month: 7, day: 1, yearOffset: 0 }, end: { month: 9, day: 30, yearOffset: 0 } },
  3: { start: { month: 10, day: 1, yearOffset: 0 }, end: { month: 12, day: 31, yearOffset: 0 } },
  4: { start: { month: 1, day: 1, yearOffset: 1 }, end: { month: 3, day: 31, yearOffset: 1 } },
};

const boundaries = (quartersType: MtdItQuarters) =>
  quartersType === MtdItQuarters.Calendar ? CALENDAR_BOUNDARIES : TAX_YEAR_BOUNDARIES;

export const formatMtdItQuarterLabel = ({ taxYearStart, quarter }: MtdItQuarterId): string => {
  const yy1 = String(taxYearStart % 100).padStart(2, '0');
  const yy2 = String((taxYearStart + 1) % 100).padStart(2, '0');
  return `${yy1}/${yy2} Q${quarter}`;
};

export const getMtdItQuarterBoundary = (
  { taxYearStart, quarter }: MtdItQuarterId,
  kind: MtdItQuarterKind,
  quartersType: MtdItQuarters,
): Date => {
  const md = boundaries(quartersType)[quarter][kind];
  return new Date(Date.UTC(taxYearStart + md.yearOffset, md.month - 1, md.day));
};

export const matchMtdItQuarter = (value: Date | string, quartersType: MtdItQuarters): MtdItQuarterId | null => {
  const date = typeof value === 'string' ? new Date(value) : value;
  if (isNaN(date.getTime())) return null;

  const anchor = new Date(date.getTime());
  if (quartersType === MtdItQuarters.TaxYear) {
    anchor.setUTCDate(anchor.getUTCDate() - 5);
  }

  const year = anchor.getUTCFullYear();
  const month = anchor.getUTCMonth() + 1;
  const taxYearStart = month >= 4 ? year : year - 1;
  const quarter = Math.floor(((((month - 4) % 12) + 12) % 12) / 3) + 1;

  return { taxYearStart, quarter: quarter as MtdItQuarterNumber };
};

type GetOptionsArgs = {
  kind: MtdItQuarterKind;
  quartersType: MtdItQuarters;
  currentValue?: Date | string | null;
  referenceYear?: number;
};

export const mtdItQuarterEditableProps = <T extends { mtdItQuarters: MtdItQuarters }>(
  model: T,
  field: keyof T,
  kind: MtdItQuarterKind,
): DropDownEditableProps => {
  const raw = model[field] as unknown as Date | string | null | undefined;

  return {
    options: getMtdItQuarterOptions({
      kind,
      quartersType: model.mtdItQuarters,
      currentValue: raw ?? undefined,
    }),
    valueOverride: raw ? formatIsoDateTime(raw) : null,
  };
};

export const getMtdItQuarterOptions = ({
  kind,
  quartersType,
  currentValue,
  referenceYear = new Date().getUTCFullYear(),
}: GetOptionsArgs): EditableOption[] => {
  const options: EditableOption[] = [];

  const firstYear = Math.max(MTD_IT_DROPDOWN_MIN_TAX_YEAR, referenceYear - MTD_IT_DROPDOWN_YEARS_BACK);
  const lastYear = referenceYear + MTD_IT_DROPDOWN_YEARS_FORWARD;

  for (let taxYearStart = firstYear; taxYearStart <= lastYear; taxYearStart++) {
    for (const quarter of [1, 2, 3, 4] as const) {
      const id: MtdItQuarterId = { taxYearStart, quarter };
      options.push({
        value: formatIsoDateTime(getMtdItQuarterBoundary(id, kind, quartersType)),
        text: formatMtdItQuarterLabel(id),
      });
    }
  }

  if (currentValue) {
    const currentValueStr = formatIsoDateTime(currentValue);

    if (!options.some((option) => option.value === currentValueStr)) {
      const matched = matchMtdItQuarter(currentValue, quartersType);
      options.unshift({
        value: currentValueStr,
        text: matched ? formatMtdItQuarterLabel(matched) : `Custom: ${formatDate(currentValue)}`,
      });
    }
  }

  return options;
};
